// Year-end projection for the ฿1.8M VAT threshold watch (plan §4.16, §0.12).
//
// Replaces "year-to-date ÷ current month number × 12". On 2 September 2026
// that divided seven complete months plus a partly synced August by nine and
// showed disorn at ฿1.74M for the year when the run-rate said ฿2.1M — an
// understatement, which is the one direction a threshold watch must never
// err in.
//
// The run-rate now comes from complete calendar months only, and only from
// the ones that look synced. The finance sync is manual, month by month, and
// lags the platforms, so a complete month can hold nothing or a fraction:
// a month with no revenue at all, or one far below the typical synced month,
// is left out wherever in the year it sits. The result says which months it
// used, so the label under the bar can say so too. Still deliberately crude:
// its job is to raise a question early, not to be a forecast.

export interface MonthRevenue {
    // Calendar month as YYYY-MM, Bangkok-local — the key the finance-tx
    // Lambda returns in calendarYearByEntity[].months.
    month: string;
    buyerTotal: number;
    // The VAT base, which is what the threshold counts. Absent from a
    // Lambda older than 2026-09-13.
    taxableConsideration?: number;
}

export type ProjectionBasis =
    // A past year: the total is final, nothing to project.
    | 'closed-year'
    // Average of the usable complete months, times twelve.
    | 'complete-months'
    // Pro rata by days elapsed — January, or a Lambda that returns no
    // per-month split (the two deploy independently, so an older one is
    // possible).
    | 'elapsed-days';

export interface YearEndProjection {
    value: number;
    basis: ProjectionBasis;
    // How many complete months the average was taken over ('complete-months'
    // only; 0 otherwise).
    monthsUsed: number;
    // Complete months left out because they look unsynced or partially
    // synced, as YYYY-MM, in calendar order.
    partialMonths: string[];
}

// A complete month below this share of the typical synced month is treated
// as partially synced rather than as a genuinely bad month. Getting that
// wrong overstates the projection, which for a threshold watch is the safe
// side to err on — and the label names the month either way.
export const PARTIAL_MONTH_RATIO = 0.5;

// With no more months holding revenue than this a low one cannot be told
// from noise, so only the empty ones are left out.
const MIN_MONTHS_TO_JUDGE = 2;

const MS_PER_DAY = 86_400_000;

export const monthKey = (year: number, month: number): string =>
    `${year}-${String(month).padStart(2, '0')}`;

const isLeapYear = (year: number): boolean =>
    (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const projectYearEnd = (
    year: number,
    revenue: number,
    months: readonly MonthRevenue[] | undefined,
    today: Date = new Date(),
): YearEndProjection => {
    const thisYear = today.getFullYear();
    if (year < thisYear) {
        return {
            value: revenue,
            basis: 'closed-year',
            monthsUsed: 12,
            partialMonths: [],
        };
    }
    if (year === thisYear && months) {
        const fromMonths = projectFromCompleteMonths(
            year,
            months,
            today.getMonth(),
        );
        if (fromMonths) {
            // The year cannot end below what is already booked.
            return {
                ...fromMonths,
                value: Math.max(fromMonths.value, revenue),
            };
        }
    }
    return projectFromElapsedDays(year, revenue, today);
};

const projectFromCompleteMonths = (
    year: number,
    months: readonly MonthRevenue[],
    completeMonths: number, // January → 0
): YearEndProjection | null => {
    if (completeMonths < 1) {
        return null;
    }
    const byMonth = new Map(
        months.map((m) => [m.month, m.taxableConsideration ?? m.buyerTotal]),
    );
    // A month with no rows is a month with nothing synced: zero, not absent.
    const series = Array.from({ length: completeMonths }, (_, i) => {
        const key = monthKey(year, i + 1);
        return { key, value: byMonth.get(key) ?? 0 };
    });
    // The yardstick is the typical month among those holding revenue, and
    // only those. Every earlier version measured against something a gap
    // could move: the mean of the prior months let three empty months excuse
    // a partly synced August (฿1.776M shown, ฿2.04M run-rate), and the median
    // of all months went to zero — judging nothing — once more than half the
    // year was unsynced (฿765k shown over "8 เดือนเต็ม", same run-rate).
    const holding = series.filter((m) => m.value > 0);
    if (holding.length === 0) {
        return null;
    }
    const floor =
        holding.length > MIN_MONTHS_TO_JUDGE
            ? median(holding.map((m) => m.value)) * PARTIAL_MONTH_RATIO
            : 0;
    const used = holding.filter((m) => m.value >= floor);
    const usedKeys = new Set(used.map((m) => m.key));
    const sum = used.reduce((a, m) => a + m.value, 0);
    return {
        value: (sum / used.length) * 12,
        basis: 'complete-months',
        monthsUsed: used.length,
        partialMonths: series
            .filter((m) => !usedKeys.has(m.key))
            .map((m) => m.key),
    };
};

const median = (values: readonly number[]): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
};

const projectFromElapsedDays = (
    year: number,
    revenue: number,
    today: Date,
): YearEndProjection => {
    const yearStart = new Date(year, 0, 1);
    const elapsed = Math.floor(
        (today.getTime() - yearStart.getTime()) / MS_PER_DAY,
    );
    // A future year has no elapsed days; report the (presumably zero) total
    // as it is rather than divide by nothing.
    const dayOfYear = Math.max(1, elapsed + 1);
    const daysInYear = isLeapYear(year) ? 366 : 365;
    return {
        value: elapsed < 0 ? revenue : (revenue / dayOfYear) * daysInYear,
        basis: 'elapsed-days',
        monthsUsed: 0,
        partialMonths: [],
    };
};
